// PermitsInfoModal.js
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalContent,
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Tabs,
  Tab,
} from "@heroui/react";
import { useEffect, useState } from "react";
import { EditableSelectComponent } from "..";
import SearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/searchComponent";
import { ResourceType } from "shared/types/resource";
import ButtonNew from "components/buttonNew/buttonNew";
import { Checkmark, External, Trash, Download, Upload } from "assets/icons";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import MultiSelectPhotos from "shared/components/multiSelectPhotos/multiSelectPhotos";
import { SecondaryLabel } from "shared/types/file";
import { DraftDocument, DraftDocumentType, DraftDocumentStatus } from "shared/types/draft-document";
import DocumentSuccessModal from "./documentSuccessModal";

export enum DocumentCategory {
  UNIT = "Unit",
  BUILDING = "Gebouw",
  ACQUISITION = "Acquisitie",
  RENOVATION = "Renovatie",
  CONTRACT = "Contract",
}

export enum DocumentName {
  INGOING_INSPECTION = "Verhuur - Ingaande plaatsbeschrijving",
  BID_LETTER_ACQ = "Acquisitie - Biedbrief",
  REQUEST_FOR_PROPOSAL = "Renovatie - Request for proposal",
  ACQUISITION_FICHE = "Acquisitie - Fiche Raad van Bestuur",
  RENTAL_CONTRACT_RESIDENTIAL = "Verhuur - Huurovereenkomst Residentieel",
  RENTAL_CONTRACT_RETAIL = "Verhuur - Huurovereenkomst Commercieel",
  REQUEST_JUSTICE_OF_PEACE_RESIDENTIAL = "Verhuur - Verzoekschrift Vrederechter Residentieel",
}

const documents = [
  {
    category: DocumentCategory.ACQUISITION,
    name: DocumentName.BID_LETTER_ACQ,
    endpoint: "acquisitions/generate-bid-letter",
  },
  {
    category: DocumentCategory.ACQUISITION,
    name: DocumentName.ACQUISITION_FICHE,
    endpoint: "acquisitions/generate-acquisition-fiche",
  },
  {
    category: DocumentCategory.RENOVATION,
    name: DocumentName.REQUEST_FOR_PROPOSAL,
    // endpoint: "acquisitions/generate-bid-letter",
  },
  {
    category: DocumentCategory.CONTRACT,
    name: DocumentName.REQUEST_JUSTICE_OF_PEACE_RESIDENTIAL,
    endpoint: "documents/generate-request-justice-of-peace-residential",
  },
  {
    category: DocumentCategory.CONTRACT,
    name: DocumentName.INGOING_INSPECTION,
    endpoint: "documents/generate-incoming-place-description",
  },
  {
    category: DocumentCategory.CONTRACT,
    name: DocumentName.RENTAL_CONTRACT_RESIDENTIAL,
    endpoint: "documents/generate-rental-contract",
  },
  {
    category: DocumentCategory.CONTRACT,
    name: DocumentName.RENTAL_CONTRACT_RETAIL,
    endpoint: "documents/generate-rental-contract",
  },
];

const DocumentGeneratorModal = ({
  isOpen,
  onClose,
  unit,
  contract,
  acquisitionId,
  building,
  documentCategory,
}: any) => {
  const [selectedDocumentCategory, setSelectedDocumentCategory] =
    useState(documentCategory);
  const [selectedDocument, setSelectedDocument] = useState<string | null>("");
  const [selectedAcquisitionId, setSelectedAcquisitionId] = useState<any>(null);
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [selectedPictures, setSelectedPictures] = useState<number[]>([]);
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [incomingPayload, setIncomingPayload] = useState({});
  const [activeTab, setActiveTab] = useState("generate");
  const [draftDocuments, setDraftDocuments] = useState<DraftDocument[]>([]);
  const [isLoadingDrafts, setIsLoadingDrafts] = useState(false);
  const [loadingOperations, setLoadingOperations] = useState<{[key: number]: {[key: string]: boolean}}>({});
  const [isCheckingStatuses, setIsCheckingStatuses] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [generatedDocument, setGeneratedDocument] = useState<{
    name: string;
    fileUrl: string;
    fileId?: string;
  } | null>(null);
  const { session } = useSession();

  // Helper functions for loading states
  const setOperationLoading = (draftId: number, operation: string, loading: boolean) => {
    setLoadingOperations(prev => ({
      ...prev,
      [draftId]: {
        ...prev[draftId],
        [operation]: loading
      }
    }));
  };

  const isOperationLoading = (draftId: number, operation: string) => {
    return loadingOperations[draftId]?.[operation] || false;
  };

  useEffect(() => {
    setSelectedUnit(unit);
  }, [unit]);

  useEffect(() => {
    setSelectedBuilding(building);
  }, [building]);

  useEffect(() => {
    setSelectedContract(contract);
  }, [contract]);

  useEffect(() => {
    setSelectedAcquisitionId(acquisitionId);
  }, [acquisitionId]);

  useEffect(() => {
    setSelectedDocumentCategory(documentCategory);
  }, [documentCategory]);

  // Fetch draft documents when contract is available
  useEffect(() => {
    if (selectedContract?.id && activeTab === "drafts") {
      fetchDraftDocuments();
    }
  }, [selectedContract?.id, activeTab]);

  const fetchDraftDocuments = async () => {
    if (!selectedContract?.id) return;
    
    setIsLoadingDrafts(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/documents/drafts/contract/${selectedContract.id}`,
        { method: "GET" },
        session
      );
      
      if (response.ok) {
        const drafts = await response.json();
        setDraftDocuments(drafts);
        
        // Automatically check status for documents in e-sign process
        await checkAllEsignStatuses(drafts);
      }
    } catch (error) {
      console.error("Error fetching draft documents:", error);
      toast.error("Fout bij ophalen van conceptdocumenten");
    } finally {
      setIsLoadingDrafts(false);
    }
  };

  const checkAllEsignStatuses = async (drafts: DraftDocument[]) => {
    // Filter documents that might need status updates
    const documentsToCheck = drafts.filter(draft => 
      draft.status === DraftDocumentStatus.SENT_TO_ESIGN || 
      draft.status === DraftDocumentStatus.SENT_FOR_SIGNING
    );

    if (documentsToCheck.length === 0) return;

    setIsCheckingStatuses(true);
    console.log(`🔄 Checking status for ${documentsToCheck.length} documents...`);

    try {
      // Check status for each document
      const statusPromises = documentsToCheck.map(async (draft) => {
        try {
          const response = await fetchWithAuth(
            `${API_URL}/documents/drafts/${draft.id}/check-esign-status`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ userId: session?.user?.id })
            },
            session
          );

          if (response.ok) {
            const updatedDraft = await response.json();
            return updatedDraft;
          }
          return draft; // Return original if check failed
        } catch (error) {
          console.error(`Error checking status for draft ${draft.id}:`, error);
          return draft; // Return original if check failed
        }
      });

      const updatedDrafts = await Promise.all(statusPromises);
      
      // Update the drafts list with new statuses
      setDraftDocuments(prevDrafts => {
        const updatedDraftsMap = new Map(updatedDrafts.map(draft => [draft.id, draft]));
        return prevDrafts.map(draft => updatedDraftsMap.get(draft.id) || draft);
      });

      // Count how many were actually updated
      const updatedCount = updatedDrafts.filter((updated, index) => 
        updated.status !== documentsToCheck[index].status
      ).length;

      if (updatedCount > 0) {
        console.log(`✅ Updated status for ${updatedCount} documents`);
        toast.success(`Status bijgewerkt voor ${updatedCount} document${updatedCount > 1 ? 'en' : ''}`);
      }

    } catch (error) {
      console.error("Error checking e-sign statuses:", error);
    } finally {
      setIsCheckingStatuses(false);
    }
  };

  const handleRefreshStatus = async (draftDocument: DraftDocument) => {
    setOperationLoading(draftDocument.id, 'refresh', true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/documents/drafts/${draftDocument.id}/check-esign-status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: session?.user?.id })
        },
        session
      );

      if (response.ok) {
        const updatedDraft = await response.json();
        
        // Update the specific document in the list
        setDraftDocuments(prevDrafts => 
          prevDrafts.map(draft => 
            draft.id === draftDocument.id ? updatedDraft : draft
          )
        );

        if (updatedDraft.status !== draftDocument.status) {
          toast.success("Status succesvol bijgewerkt");
        } else {
          toast.info("Status is al up-to-date");
        }
      } else {
        toast.error("Fout bij bijwerken van status");
      }
    } catch (error) {
      console.error("Error refreshing status:", error);
      toast.error("Fout bij bijwerken van status");
    } finally {
      setOperationLoading(draftDocument.id, 'refresh', false);
    }
  };

  const handleClose = () => {
    setIsLoading(false);
    setSelectedDocument(null);
    setSelectedAcquisitionId(null);
    setSelectedUnit(null);
    setSelectedBuilding(null);
    setSelectedContract(null);
    setSelectedPictures([]);
    setIncomingPayload({});
    setActiveTab("generate");
    setDraftDocuments([]);
    setLoadingOperations({}); // Clear all loading states
    setIsCheckingStatuses(false);
    onClose && onClose();
  }

  const handleGenerateDraft = async () => {
    if (!selectedDocument || !selectedContract?.id || !selectedUnit?.id) {
      toast.error("Selecteer een document type, contract en unit");
      return;
    }

    setIsLoading(true);
    try {
      const documentType = getDocumentTypeFromName(selectedDocument);
      if (!documentType) {
        toast.error("Ongeldig document type");
        return;
      }

      const response = await fetchWithAuth(
        `${API_URL}/documents/drafts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            contractId: selectedContract.id,
            unitId: selectedUnit.id,
            documentType,
            userId: session?.user?.id
          })
        },
        session
      );

      if (response.ok) {
        await response.json();
        toast.success("Conceptdocument succesvol aangemaakt");
        
        // Switch to drafts tab and refresh
        setActiveTab("drafts");
        fetchDraftDocuments();
      } else {
        const error = await response.json();
        toast.error(error.message || "Fout bij aanmaken conceptdocument");
      }
    } catch (error) {
      console.error("Error creating draft document:", error);
      toast.error("Fout bij aanmaken conceptdocument");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDraftDocument = (draftDocument: DraftDocument) => {
    // For finalized documents, prioritize the PDF over the Word document
    if (draftDocument.status === DraftDocumentStatus.FINALIZED && draftDocument.finalSharePointFileUrl) {
      window.open(draftDocument.finalSharePointFileUrl, '_blank');
    } else if (draftDocument.draftSharePointFileUrl) {
      window.open(draftDocument.draftSharePointFileUrl, '_blank');
    } else if (draftDocument.finalSharePointFileUrl) {
      window.open(draftDocument.finalSharePointFileUrl, '_blank');
    }
  };

  const handleDownloadDraftDocument = async (draftDocument: DraftDocument) => {
    if (!draftDocument.draftSharePointFileId) {
      toast.error("Geen Word document beschikbaar om te downloaden");
      return;
    }

    setOperationLoading(draftDocument.id, 'download', true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/documents/drafts/${draftDocument.id}/download`,
        { method: 'GET' },
        session
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${draftDocument.title}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Document succesvol gedownload");
      } else {
        toast.error("Fout bij downloaden van document");
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error("Fout bij downloaden van document");
    } finally {
      setOperationLoading(draftDocument.id, 'download', false);
    }
  };

  const handleUploadDraftDocument = (draftDocument: DraftDocument) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.docx,.doc';
    input.onchange = async (event: any) => {
      const file = event.target.files[0];
      if (!file) return;

      setOperationLoading(draftDocument.id, 'upload', true);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetchWithAuth(
          `${API_URL}/documents/drafts/${draftDocument.id}/upload`,
          {
            method: 'POST',
            body: formData,
          },
          session
        );

        if (response.ok) {
          toast.success("Document succesvol geüpload en bijgewerkt");
          fetchDraftDocuments(); // Refresh the list
        } else {
          const error = await response.json();
          toast.error(error.message || "Fout bij uploaden van document");
        }
      } catch (error) {
        console.error('Error uploading document:', error);
        toast.error("Fout bij uploaden van document");
      } finally {
        setOperationLoading(draftDocument.id, 'upload', false);
      }
    };
    input.click();
  };

  const handleFinalizeDraft = async (draftDocument: DraftDocument) => {
    setOperationLoading(draftDocument.id, 'finalize', true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/documents/drafts/${draftDocument.id}/finalize`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: session?.user?.id
          })
        },
        session
      );

      if (response.ok) {
        toast.success("Document succesvol gefinaliseerd");
        fetchDraftDocuments();
      } else {
        const error = await response.json();
        toast.error(error.message || "Fout bij finaliseren document");
      }
    } catch (error) {
      console.error("Error finalizing draft document:", error);
      toast.error("Fout bij finaliseren document");
    } finally {
      setOperationLoading(draftDocument.id, 'finalize', false);
    }
  };

  const handleDeleteDraft = async (draftDocument: DraftDocument) => {
    if (!confirm("Weet je zeker dat je dit conceptdocument wilt verwijderen?")) {
      return;
    }

    setOperationLoading(draftDocument.id, 'delete', true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/documents/drafts/${draftDocument.id}`,
        { method: "DELETE" },
        session
      );

      if (response.ok) {
        toast.success("Conceptdocument succesvol verwijderd");
        fetchDraftDocuments();
      } else {
        const error = await response.json();
        toast.error(error.message || "Fout bij verwijderen conceptdocument");
      }
    } catch (error) {
      console.error("Error deleting draft document:", error);
      toast.error("Fout bij verwijderen conceptdocument");
    } finally {
      setOperationLoading(draftDocument.id, 'delete', false);
    }
  };

  const handleSendForEsign = async (draftDocument: DraftDocument) => {
    if (!confirm("Weet je zeker dat je dit document wilt versturen voor ondertekening?")) {
      return;
    }

    setOperationLoading(draftDocument.id, 'esign', true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/documents/drafts/${draftDocument.id}/send-for-esign`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: session?.user?.id
          })
        },
        session
      );

      if (response.ok) {
        toast.success("Document succesvol verstuurd voor ondertekening");
        fetchDraftDocuments();
      } else {
        const error = await response.json();
        toast.error(error.message || "Fout bij versturen document voor ondertekening");
      }
    } catch (error) {
      console.error("Error sending document for e-sign:", error);
      toast.error("Fout bij versturen document voor ondertekening");
    } finally {
      setOperationLoading(draftDocument.id, 'esign', false);
    }
  };

  const getDocumentTypeFromName = (documentName: string): DraftDocumentType | null => {
    switch (documentName) {
      case DocumentName.RENTAL_CONTRACT_RESIDENTIAL:
        return DraftDocumentType.RENTAL_CONTRACT_RESIDENTIAL;
      case DocumentName.RENTAL_CONTRACT_RETAIL:
        return DraftDocumentType.RENTAL_CONTRACT_RETAIL;
      case DocumentName.INGOING_INSPECTION:
        return DraftDocumentType.INGOING_INSPECTION;
      case DocumentName.REQUEST_JUSTICE_OF_PEACE_RESIDENTIAL:
        return DraftDocumentType.JUSTICE_OF_PEACE_REQUEST;
      default:
        return null;
    }
  };

  const getStatusColor = (status: DraftDocumentStatus) => {
    switch (status) {
      case DraftDocumentStatus.DRAFT:
        return "default";
      case DraftDocumentStatus.READY_FOR_FINALIZATION:
        return "warning";
      case DraftDocumentStatus.FINALIZED:
        return "success";
      case DraftDocumentStatus.SENT_TO_ESIGN:
        return "secondary";
      case DraftDocumentStatus.SENT_FOR_SIGNING:
        return "primary";
      case DraftDocumentStatus.SIGNED:
        return "success";
      case DraftDocumentStatus.CANCELLED:
        return "danger";
      default:
        return "default";
    }
  };

  const getStatusText = (status: DraftDocumentStatus) => {
    switch (status) {
      case DraftDocumentStatus.DRAFT:
        return "Concept";
      case DraftDocumentStatus.READY_FOR_FINALIZATION:
        return "Klaar voor finalisatie";
      case DraftDocumentStatus.FINALIZED:
        return "Gefinaliseerd";
      case DraftDocumentStatus.SENT_TO_ESIGN:
        return "Verzonden naar e-sign";
      case DraftDocumentStatus.SENT_FOR_SIGNING:
        return "Verzonden voor ondertekening";
      case DraftDocumentStatus.SIGNED:
        return "Ondertekend";
      case DraftDocumentStatus.CANCELLED:
        return "Geannuleerd";
      default:
        return status;
    }
  };

  const isContractDocument = (documentName: string) => {
    return documentName === DocumentName.RENTAL_CONTRACT_RESIDENTIAL || 
           documentName === DocumentName.RENTAL_CONTRACT_RETAIL;
  };

  const handleGenerate = async () => {
    console.log("Generating " + selectedDocument);
    setIsLoading(true); // Start loading

    const selectedDocumentInfo = documents.find(
      (document) => document.name === selectedDocument,
    );
    if (!selectedDocumentInfo) {
      setIsLoading(false);
      return;
    }

    let payload = {
      acquisitionId: selectedAcquisitionId && selectedAcquisitionId,
      unitId: selectedUnit && selectedUnit.id,
      contractId: selectedContract && selectedContract.id,
      buildingId: selectedBuilding && selectedBuilding.id,
      selectedPictures: selectedPictures, // send to backend but currently has no use as the backend will select on secondary labels in the database
      ...incomingPayload
    };

    console.log(payload);

    try {
      const res = await fetchWithAuth(
        `${API_URL}/${selectedDocumentInfo.endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
        session,
      );

      if (!res.ok) {
        throw new Error("Server error when generating bid letter");
      }

      // Assuming the response includes the file URL directly
      const { fileUrl, fileId } = await res.json();

      // Check if the URL is valid
      if (fileUrl) {
        setGeneratedDocument({
          name: selectedDocument || "Document",
          fileUrl: fileUrl,
          fileId: fileId
        });
        setShowSuccessModal(true);
        toast.success(`${selectedDocument} gegenereerd!`);
      } else {
        throw new Error("Kan gegenereerd document niet openen.");
      }
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het genereren van het document",
      );
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const pictureSelection = () => {
    if(selectedDocument == DocumentName.INGOING_INSPECTION) return true;
    if(selectedDocument == DocumentName.REQUEST_FOR_PROPOSAL) return true;
    if(selectedDocument == DocumentName.ACQUISITION_FICHE) return true;
    return false;
  }

  const getSecondaryLabel = () => {
    if(selectedDocument == DocumentName.INGOING_INSPECTION) return SecondaryLabel.INGOING_PLACE_DESCRIPTION;
    if(selectedDocument == DocumentName.REQUEST_FOR_PROPOSAL) return SecondaryLabel.RENOVATIE;
    if(selectedDocument == DocumentName.ACQUISITION_FICHE) return SecondaryLabel.ACQUISITIEFICHE;
    return false;
  }

  // Photos uploaded under these file labels are always included by the backend
  // generator for the selected document, without needing a secondary tag.
  const getAlwaysIncludedFileLabels = () => {
    if(selectedDocument == DocumentName.INGOING_INSPECTION) return ["Fotos ingaande plaatsbeschrijving"];
    return undefined;
  }

  const handleSelectedDocument = (documentName: string) => {
    setSelectedDocument(documentName);
  };

  const showBuildingSearch = () => {
    if (acquisitionId) return false;
  };

  const showUnitSearch = () => {
    if (acquisitionId) return false;
  };

  // const handlePayLoadChange = (value: any) => {
  //   setIncomingPayload(value);
  // }

  const handlePictureSelect = (id: number) => {
    setSelectedPictures((prev) =>
      prev.includes(id)
        ? prev.filter((photoId) => photoId !== id)
        : [...prev, id],
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      shouldBlockScroll={true}
      isDismissable={true}
      scrollBehavior="inside"
      backdrop="blur"
      className="p-8"
    >
      <ModalContent className="flex flex-col gap-6 font-normal text-neutral-primary">
        <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
          <p>Documentgenerator</p>
          <div className="flex gap-2">
            {activeTab === "generate" && selectedDocument && (
              <>
                <ButtonNew
                  icon={<Checkmark />}
                  label={`Genereer ${isContractDocument(selectedDocument || '') ? 'concept' : ''} ${selectedDocument}`}
                  onClick={isContractDocument(selectedDocument || '') ? handleGenerateDraft : handleGenerate}
                  variant="primaryMoss"
                />
                {!isContractDocument(selectedDocument || '') && (
                  <ButtonNew
                    icon={<Checkmark />}
                    label={`Genereer direct ${selectedDocument}`}
                    onClick={handleGenerate}
                    variant="secondaryMoss"
                  />
                )}
              </>
            )}
          </div>
        </ModalHeader>
        <ModalBody className="px-0 py-0">
          {isLoading ? (
            <Spinner label={`${selectedDocument} aan het genereren`} />
          ) : (
            <Tabs 
              selectedKey={activeTab} 
              onSelectionChange={(key) => setActiveTab(key as string)}
              className="w-full"
            >
              <Tab key="generate" title="Document genereren">
                <section className="flex flex-col gap-6 rounded-md">
              {showBuildingSearch() && (
                <SearchComponent
                  type="regular"
                  label="Gebouw"
                  resourceFilter={[ResourceType.BUILDING]}
                  initialValue={(selectedBuilding && selectedBuilding.id) || ""}
                  onSelect={(resource) => {
                    setSelectedBuilding(() => {
                      id: resource.resourceId;
                    });
                  }}
                  mode={SearchMode.SEARCH_AND_PRESENT}
                  editMode={true}
                />
              )}
              {showUnitSearch() && (
                <SearchComponent
                  type="regular"
                  label="Unit"
                  resourceFilter={[ResourceType.UNIT]}
                  initialValue={(selectedUnit && selectedUnit.id) || ""}
                  onSelect={(resource) => {
                    setSelectedUnit(() => {
                      id: resource.resourceId;
                    });
                  }}
                  mode={SearchMode.SEARCH_AND_PRESENT}
                  editMode={true}
                />
              )}

              <EditableSelectComponent
                label="Selecteer document om te genereren"
                editMode={true}
                value={selectedDocument || ""}
                options={
                  documents &&
                  documents
                    .filter(
                      (document) =>
                        document.category == selectedDocumentCategory,
                    )
                    .map((document) => ({
                      label: document.name,
                      value: document.name,
                    }))
                }
                onChange={(documentName) =>
                  handleSelectedDocument(documentName)
                }
              />

              {/* Information box for "Genereer direct" */}
              {selectedDocument && !isContractDocument(selectedDocument) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start">
                    <div className="shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Keuze tussen twee opties
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>
                          <strong>Genereer:</strong> Maakt een concept aan dat je kunt bewerken voordat het definitief wordt.
                        </p>
                        <p className="mt-1">
                          <strong>Genereer direct:</strong> Maakt het document direct aan en slaat het op zonder bewerkingsmogelijkheid.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Can reuse later to select contacts here */}
              {/* {selectedDocument == DocumentName.INGOING_INSPECTION && (
                <IngoingInspectionFields handlePayloadChange={handlePayLoadChange} incomingBuildingId={selectedBuilding && selectedBuilding.id || ""} incomingUnitId={selectedUnit && selectedUnit.id || ""} handlePictureSelect={handlePictureSelect} />
              )} */}
              {pictureSelection() && getSecondaryLabel() && <MultiSelectPhotos incomingBuildingId={selectedBuilding?.id || ""} incomingAcquisitionId={acquisitionId || ""} incomingUnitId={selectedUnit?.id || ""} incomingSecondaryLabel={getSecondaryLabel() as any} incomingAlwaysIncludedFileLabels={getAlwaysIncludedFileLabels()} handlePictureSelect={(id: number) => handlePictureSelect(id)} />}
                </section>
              </Tab>
              
              {selectedContract && (
                <Tab key="drafts" title="Conceptdocumenten">
                  <section className="flex flex-col gap-6 rounded-md">
                                            {isLoadingDrafts ? (
                          <Spinner label="Conceptdocumenten aan het laden..." />
                        ) : isCheckingStatuses ? (
                          <div className="flex items-center justify-center py-4">
                            <Spinner size="sm" className="mr-2" />
                            <span className="text-sm text-neutral-secondary-mid">Status controleren...</span>
                          </div>
                        ) : (
                      <>
                        {draftDocuments.length === 0 ? (
                          <div className="text-center py-8 text-neutral-secondary-mid">
                            <p>Nog geen conceptdocumenten aangemaakt voor dit contract.</p>
                            <p className="text-sm mt-2">Ga naar het "Document genereren" tabblad om een huurcontract concept aan te maken.</p>
                          </div>
                        ) : (
                          <Table aria-label="Draft documents table">
                            <TableHeader>
                              <TableColumn>Document</TableColumn>
                              <TableColumn>Status</TableColumn>
                              <TableColumn>Aangemaakt</TableColumn>
                              <TableColumn>Laatst bewerkt</TableColumn>
                              <TableColumn>Acties</TableColumn>
                            </TableHeader>
                            <TableBody>
                              {draftDocuments.map((draft) => (
                                <TableRow key={draft.id}>
                                  <TableCell>
                                    <div className="flex flex-col">
                                      <span className="font-medium">{draft.title}</span>
                                      <span className="text-xs text-neutral-secondary-mid">
                                        {draft.documentType.replace(/_/g, ' ')}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      color={getStatusColor(draft.status) as any}
                                      size="sm"
                                    >
                                      {getStatusText(draft.status)}
                                    </Chip>
                                  </TableCell>
                                  <TableCell>
                                    <span className="text-sm">
                                      {new Date(draft.createdAt).toLocaleDateString('nl-NL')}
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <span className="text-sm">
                                      {draft.lastEditedAt 
                                        ? new Date(draft.lastEditedAt).toLocaleDateString('nl-NL')
                                        : '-'
                                      }
                                    </span>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-2 flex-wrap">
                                      {/* PDF/Word Open Button */}
                                      {(draft.status === DraftDocumentStatus.FINALIZED || 
                                        draft.status === DraftDocumentStatus.SENT_TO_ESIGN || 
                                        draft.status === DraftDocumentStatus.SENT_FOR_SIGNING ||
                                        draft.status === DraftDocumentStatus.SIGNED ||
                                        draft.status === DraftDocumentStatus.CANCELLED) && (
                                        <ButtonNew
                                          icon={<External />}
                                          onClick={() => handleOpenDraftDocument(draft)}
                                          variant="secondaryNeutral"
                                          label="PDF Openen"
                                        />
                                      )}
                                      {draft.status === DraftDocumentStatus.DRAFT && (
                                        <ButtonNew
                                          icon={<External />}
                                          onClick={() => handleOpenDraftDocument(draft)}
                                          variant="secondaryNeutral"
                                          label="Word Openen"
                                        />
                                      )}

                                      {/* Odoo eSign Template Link */}
                                      {draft.status === DraftDocumentStatus.SENT_TO_ESIGN && draft.odooSignUrl && (
                                        <ButtonNew
                                          icon={<External />}
                                          onClick={() => window.open(draft.odooSignUrl, '_blank')}
                                          variant="primaryMoss"
                                          label="Odoo Template"
                                        />
                                      )}

                                      {/* Odoo Sign Request Link - when a sign request is created */}
                                      {(draft.status === DraftDocumentStatus.SENT_FOR_SIGNING || 
                                        draft.status === DraftDocumentStatus.SIGNED ||
                                        draft.status === DraftDocumentStatus.CANCELLED) && 
                                        draft.odooSignRequestId && (
                                        <ButtonNew
                                          icon={<External />}
                                          onClick={() => {
                                            const baseUrl = process.env.NODE_ENV === 'production' 
                                              ? process.env.REACT_APP_ODOO_API_URL 
                                              : process.env.REACT_APP_ODOO_API_STAGING_URL;
                                            window.open(`${baseUrl}/web#id=${draft.odooSignRequestId}&model=sign.request&view_type=form`, '_blank');
                                          }}
                                          variant="primaryMoss"
                                          label="Odoo Ondertekening"
                                        />
                                      )}

                                      {/* Status Refresh Button for e-sign documents */}
                                      {(draft.status === DraftDocumentStatus.SENT_TO_ESIGN || 
                                        draft.status === DraftDocumentStatus.SENT_FOR_SIGNING) && (
                                        <ButtonNew
                                          icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                          </svg>}
                                          onClick={() => handleRefreshStatus(draft)}
                                          variant="secondaryNeutral"
                                          label={isOperationLoading(draft.id, 'refresh') ? "Vernieuwen..." : "Status Vernieuwen"}
                                          isDisabled={isOperationLoading(draft.id, 'refresh')}
                                        />
                                      )}

                                      {/* Draft Document Actions */}
                                      {draft.status === DraftDocumentStatus.DRAFT && (
                                        <>
                                          <ButtonNew
                                            icon={<Download />}
                                            onClick={() => handleDownloadDraftDocument(draft)}
                                            variant="secondaryNeutral"
                                            label={isOperationLoading(draft.id, 'download') ? "Downloaden..." : "Download"}
                                            isDisabled={isOperationLoading(draft.id, 'download') || isOperationLoading(draft.id, 'finalize')}
                                          />
                                          <ButtonNew
                                            icon={<Upload />}
                                            onClick={() => handleUploadDraftDocument(draft)}
                                            variant="secondaryNeutral"
                                            label={isOperationLoading(draft.id, 'upload') ? "Uploaden..." : "Upload"}
                                            isDisabled={isOperationLoading(draft.id, 'upload') || isOperationLoading(draft.id, 'finalize')}
                                          />
                                          <ButtonNew
                                            icon={<Checkmark />}
                                            onClick={() => handleFinalizeDraft(draft)}
                                            variant="primaryMoss"
                                            label={isOperationLoading(draft.id, 'finalize') ? "Finaliseren..." : "Finaliseren"}
                                            isDisabled={isOperationLoading(draft.id, 'finalize') || isOperationLoading(draft.id, 'download') || isOperationLoading(draft.id, 'upload')}
                                          />
                                        </>
                                      )}

                                      {/* Send for E-sign */}
                                      {draft.status === DraftDocumentStatus.FINALIZED && (
                                        <ButtonNew
                                          icon={<Checkmark />}
                                          onClick={() => handleSendForEsign(draft)}
                                          variant="primaryMoss"
                                          label={isOperationLoading(draft.id, 'esign') ? "Versturen..." : "Verstuur voor ondertekening"}
                                          isDisabled={isOperationLoading(draft.id, 'esign')}
                                        />
                                      )}

                                      {/* Delete Button */}
                                      {(draft.status === DraftDocumentStatus.DRAFT || 
                                        draft.status === DraftDocumentStatus.READY_FOR_FINALIZATION) && (
                                        <ButtonNew
                                          icon={<Trash />}
                                          onClick={() => handleDeleteDraft(draft)}
                                          variant="secondaryBrick"
                                          label={isOperationLoading(draft.id, 'delete') ? "Verwijderen..." : "Verwijderen"}
                                          isDisabled={isOperationLoading(draft.id, 'delete') || isOperationLoading(draft.id, 'finalize') || isOperationLoading(draft.id, 'download') || isOperationLoading(draft.id, 'upload')}
                                        />
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </>
                    )}
                  </section>
                </Tab>
              )}
            </Tabs>
          )}
        </ModalBody>
      </ModalContent>
      
      {/* Success Modal */}
      {generatedDocument && (
        <DocumentSuccessModal
          isOpen={showSuccessModal}
          onClose={handleClose}
          documentName={generatedDocument.name}
          fileUrl={generatedDocument.fileUrl}
          fileId={generatedDocument.fileId}
        />
      )}
    </Modal>
  );
};

export default DocumentGeneratorModal;
